import React, { useMemo } from 'react';

import BuyingTool from '@/components/sections/shared/buying-tool';
import { Icon } from '@/components/shared/element/icons';
import { Link } from '@/components/shared/element/link';
import { Text } from '@/components/shared/element/text';
import { Box } from '@/components/shared/layout/box';
import { Container } from '@/components/shared/layout/container';
import { Flex } from '@/components/shared/layout/flex';
import { Grid } from '@/components/shared/layout/grid';
import { Section } from '@/components/shared/layout/section';
import { Image } from '@/components/shared/media/image';
import { useWindowSize } from '@/components/shared/utility/hooks';
import { styled } from '@/stitches.config';
import { IMenu } from '@/types/layout/menu';

interface IProps {
  footerContact?: IMenu;
  footerCopyright?: string;
  carModelMenu?: IMenu;
  serviceMenu?: IMenu;
  offerMenu?: IMenu;
  privilegeMenu?: IMenu;
  contentHubMenu?: IMenu;
  lifeCycleMenu?: IMenu;
  insuranceMenu?: IMenu;
  faqMenu?: IMenu;
  otherServiceMenu?: IMenu;
  aboutMenu?: IMenu;
  socialMenu?: IMenu;
  masterofallroadsMenu?: IMenu;
  buyingToolMenu?: IMenu[];
  footerAbout?: IMenu;
  footerRemark?: string;
}

const SubMenuListInner = styled(Grid, {
  rowGap: '6px',
});

const SubMenuList = styled(Grid, {
  borderTopWidth: '1px',
  borderTopStyle: 'solid',
  borderTopColor: '$gray300',
  pt: '$4',
  '&.is-icon': {
    [`& ${SubMenuListInner}`]: {
      rowGap: '$2',
    },
  },
});

const FooterInner = styled('div', {
  display: 'grid',
  rowGap: '$6',
  '& a': {
    display: 'block',
    '@hover': {
      '&:hover': {
        '& i, & p, & h3, & span': {
          color: '$primary',
        },
      },
    },
  },
  variants: {
    size: {
      2: {
        gridColumn: '1 / span 2',
        [`& ${SubMenuListInner}`]: {
          gridTemplateColumns: 'repeat(2, 1fr)',
        },
      },
    },
  },
});

const FooterDesktop = styled('div', {
  display: 'none',
  '@lg': {
    display: 'flex',
    columnGap: '$2',
    alignItems: 'flex-start',
    [`& ${FooterInner}`]: {
      flexGrow: '1',
      flexBasis: '0',
      maxWidth: '100%',
      '& > *': {
        maxWidth: '198px',
      },
    },
  },
});

const FooterMobile = styled('div', {
  display: 'grid',
  gridTemplateColumns: 'repeat(2, 1fr)',
  alignItems: 'flex-start',
  rowGap: '$6',
  columnGap: '$6',
  '@lg': {
    display: 'none',
  },
});

const LinkItem = ({ item, isSub = true }: { item: IMenu; isSub?: boolean }) => {
  return (
    <Link
      href={item?.url}
      target={item?.target}
      tracking={{
        dataTrack: 'footer',
        dataTrackText: item?.label,
        dataTrackUrl: item?.url,
      }}
    >
      {item?.icon ? (
        <Flex gap="2">
          <Icon size="sm" icon={item?.icon} color="icon-gray-light" />
          <Text as="p" size="xs" css={{ pt: 2, lineHeight: '18px' }}>
            {item?.label}
          </Text>
        </Flex>
      ) : (
        <Text
          as="p"
          size="xs"
          variant={isSub ? 'text-gray-darkest' : 'text-gray-light'}
        >
          {item?.label}
        </Text>
      )}
    </Link>
  );
};

const ListItem = ({ submenu = [], url, label, ...props }: IMenu) => {
  const hasIcon = useMemo(() => {
    return submenu.some((item) => !!item.icon);
  }, [submenu]);

  const hasSubMenu = useMemo(() => {
    return submenu.some((item) => !!item.submenu);
  }, [submenu]);

  return (
    <Grid rowGap="2">
      {!!label && (
        <>
          {url ? (
            <Link
              href={url}
              target={props.target}
              tracking={{
                dataTrack: 'footer',
                dataTrackText: label,
                dataTrackUrl: url,
              }}
            >
              <Text as="h3" size="sm" font="bold">
                {label}
              </Text>
            </Link>
          ) : (
            <Text as="h3" size="sm" font="bold">
              {label}
            </Text>
          )}
        </>
      )}

      {submenu.length > 0 && (
        <SubMenuList
          rowGap={hasSubMenu ? '3' : '2'}
          className={hasIcon ? 'is-icon' : null}
        >
          {submenu.map((item, index) => {
            return (
              <SubMenuListInner columnGap="6" key={index}>
                <LinkItem item={item} isSub={!item?.submenu} />
                {!!(item?.submenu && item?.submenu?.length > 0) &&
                  item.submenu.map((menu, index) => {
                    return <LinkItem item={menu} key={index} />;
                  })}
              </SubMenuListInner>
            );
          })}
        </SubMenuList>
      )}
    </Grid>
  );
};

const ItemLogo = (props: IMenu) => {
  return (
    <Link
      href={props.url}
      target={props.target}
      tracking={{
        dataTrack: 'footer',
        dataTrackText: props.image.alt,
        dataTrackUrl: props.url,
      }}
      css={{
        '@maxlg': {
          marginTop: '$4',
        },
      }}
    >
      <Image
        rootMargin="20% 0px 0px 0px"
        src={props?.image?.src ?? ''}
        alt={props?.image?.alt ?? ''}
        width={props?.image?.width ?? 0}
        height={props?.image?.height ?? 0}
        layout="responsive"
      />
    </Link>
  );
};

const View = (props: IProps) => {
  const windowSize = useWindowSize();
  const {
    footerContact,
    carModelMenu,
    serviceMenu,
    socialMenu,
    footerAbout,
    footerRemark,
  } = props;

  return (
    <Section as="footer">
      <BuyingTool items={props.buyingToolMenu} />
      {!!footerContact && (
        <Section
          as="div"
          backgroundColor="gray-light"
          size="sm"
          data-test="section_contactChannel"
        >
          <Container>
            <Flex
              justify={{ '@initial': 'center', '@md': 'between' }}
              align="center"
            >
              {!!footerContact?.label && (
                <Box
                  css={{
                    display: 'none',
                    '@md': {
                      display: 'block',
                    },
                  }}
                >
                  <Text as="h2" size="h5" font="bold">
                    {footerContact?.label}
                  </Text>
                </Box>
              )}

              {!!(
                footerContact?.submenu && footerContact?.submenu.length > 0
              ) && (
                <Grid
                  rowGap={{ '@initial': '6', '@sm': '8', '@lg': '9' }}
                  justifyContent="center"
                  alignItems="center"
                  css={{
                    gridTemplateColumns:
                      'minmax(124px, 198px) minmax(124px, 198px)',
                  }}
                >
                  {footerContact?.submenu.map((o, i) => (
                    <React.Fragment key={i}>
                      <Link
                        href={o.url}
                        target={o.target}
                        tracking={{
                          dataTrack: 'footer',
                          dataTrackText: o.image.alt,
                          dataTrackUrl: o.url,
                        }}
                      >
                        <Image
                          rootMargin="20% 0px 0px 0px"
                          src={o?.image?.src ?? ''}
                          alt={o?.image?.alt ?? ''}
                          width={o?.image?.width ?? 0}
                          height={o?.image?.height ?? 0}
                          layout="responsive"
                        />
                      </Link>
                    </React.Fragment>
                  ))}
                </Grid>
              )}
            </Flex>
          </Container>
        </Section>
      )}

      <Section
        as="div"
        color="white"
        css={{
          py: '$7',
          '@sm': {
            py: '$11',
          },
        }}
        data-test="section_footerMenu"
      >
        <Container>
          {windowSize.width < 1024 ? (
            <FooterMobile>
              <FooterInner>
                {!!carModelMenu && <ListItem {...carModelMenu} />}
              </FooterInner>
              <FooterInner>
                {!!serviceMenu && <ListItem {...serviceMenu} />}
              </FooterInner>
              {!!footerRemark && (
                <FooterInner size="2">
                  <Text
                    as="p"
                    size="2xs"
                    variant="text-gray-dark"
                    dangerouslySetInnerHTML={{
                      __html: props.footerRemark,
                    }}
                    css={{
                      '@lg': {
                        display: 'none',
                      },
                    }}
                  />
                </FooterInner>
              )}

              <FooterInner size="2">
                {!!socialMenu && <ListItem {...socialMenu} />}
              </FooterInner>
              <FooterInner size="2">
                {!!footerAbout && <ItemLogo {...footerAbout} />}
              </FooterInner>
            </FooterMobile>
          ) : (
            <FooterDesktop>
              <FooterInner>
                {!!carModelMenu && <ListItem {...carModelMenu} />}
              </FooterInner>
              <FooterInner>
                {!!serviceMenu && <ListItem {...serviceMenu} />}
              </FooterInner>
              <FooterInner>
                {!!socialMenu && <ListItem {...socialMenu} />}
              </FooterInner>
              <FooterInner>
                {!!footerAbout && <ItemLogo {...footerAbout} />}
              </FooterInner>
            </FooterDesktop>
          )}
        </Container>
        {!!props.footerCopyright && (
          <Box
            css={{
              mt: '$7',
              '@sm': {
                mt: '$11',
              },
            }}
          >
            <Container>
              <Flex justify={{ '@initial': 'start', '@lg': 'between' }}>
                <Text
                  as="p"
                  size="2xs"
                  variant="text-gray-dark"
                  dangerouslySetInnerHTML={{
                    __html: props.footerRemark,
                  }}
                  css={{
                    '@maxlg': {
                      display: 'none',
                    },
                  }}
                />
                <Text
                  as="p"
                  size="2xs"
                  variant="text-gray-dark"
                  dangerouslySetInnerHTML={{
                    __html: props.footerCopyright,
                  }}
                />
              </Flex>
            </Container>
          </Box>
        )}
      </Section>
    </Section>
  );
};
View.displayName = 'Footer';
export default View;
